import { useMemo } from 'react';
import { useAppContext } from '@/context';
import { Flatten, FormTruckComposition, FormTruckCompositionOwner, FormTrucker } from '@/types';
import { usePlateSearch } from '@/components/FormWrapper/hooks';

export const makeTruckCompositionOwner = (trucker?: FormTrucker): FormTruckCompositionOwner => ({
  cellPhone: trucker?.cellPhone || '',
  cpfCnpj: trucker?.cpf || '',
  name: trucker?.name || '',
  motherName: trucker?.motherName || '',
  birthdate: trucker?.birthday || '',
  rntrc: '',
  rntrcUuid: '',
  rntrcCpfCnpj: '',
  rntrcName: '',
  rntrcOwnerIsTruckOwner: false,
  address: {
    complement: trucker?.address?.complement || '',
    location: trucker?.address?.location ? { ...trucker?.address?.location } : undefined,
    neighborhood: trucker?.address?.neighborhood || '',
    number: trucker?.address?.number || '',
    street: trucker?.address?.street || '',
    zipCode: trucker?.address?.zipCode || '',
  },
});

const makeTruckComposition = (): FormTruckComposition => ({
  brand: '',
  chassis: '',
  color: undefined,
  leaserCpfCnpj: '',
  leaserName: '',
  location: undefined,
  model: '',
  modelYear: '',
  monofactureYear: '',
  owner: makeTruckCompositionOwner(),
  plate: '',
  renavam: '',
  truckerIsOwner: false,
  typeId: undefined,
  typeName: undefined,
  vehicleIsLeased: false,
  plateApi: undefined,
});

export const makeTruckCompositions = (amount: number): FormTruckComposition[] =>
  Array.from({ length: amount }, makeTruckComposition);

type DisabledFieldProps = {
  field: keyof Flatten<FormTruckComposition>;
  position: number;
  loadingPlate: boolean[];
};

export const useIsDisabledField = () => {
  const { state } = useAppContext();
  const isResubmit = useMemo(() => Boolean(state.risk.backendResponse?.uuid), [state.risk.backendResponse]);
  const { isLoadedVehicleField } = usePlateSearch(state.risk.data, state.truckTypes.data);

  const isPlateNotFilled = (position: number, loadingPlate: boolean[]) =>
    state.risk.data.truck.compositions[position]?.plate?.length !== 7 || !!loadingPlate[position];

  const isDisabledField = ({ field, position, loadingPlate }: DisabledFieldProps) => {
    if (isResubmit) return false;

    return isPlateNotFilled(position, loadingPlate) || isLoadedVehicleField(position, field);
  };

  return { isDisabledField, isResubmit, isPlateNotFilled };
};
